import axiosInstance, { API_CONFIG } from "../config/apiConfig";
import { User, UserCreate, UserUpdate } from "../models/User";
import CryptoJS from 'crypto-js';
import { CacheService } from "../utils/CacheService";


const API_BASE_URL = `${API_CONFIG.baseURL}/users`;
const STORAGE_KEY = 'sfe_user_data';
const USERS_CACHE_KEY = 'sfe_users_list';
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'default-key-replace-in-prod';

interface APIResponse<T> {
  data: T;
  message: string;
}

interface StoredUserData {
  userId: number;
  userName: string;
  email: string;
  personaId: number;
  accessLevelId: number;
  timestamp: number;
  updateAttemptedAt: number;
}

function encryptData(data: any): string {
  return CryptoJS.AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY).toString();
}

function decryptData(encryptedData: string): any {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error('Error decrypting data:', error);
    return null;
  }
}

function storeUserData(user: User | null): void {
  if (!user || !user.user_id) {
    return;
  }

  const userData: StoredUserData = {
    userId: user.user_id,
    userName: user.user_name,
    email: user.email,
    personaId: user.persona_id,
    accessLevelId: user.access_level_id,
    timestamp: Date.now(),
    updateAttemptedAt: Date.now()
  };

  const encryptedData = encryptData(userData);
  localStorage.setItem(STORAGE_KEY, encryptedData);
  localStorage.setItem("userId", user.user_id.toString());
}

const usersCache = new CacheService<User[]>(
  async () => {
    const response = await axiosInstance.get<APIResponse<User[]>>(API_BASE_URL);
    return response.data.data;
  },
  USERS_CACHE_KEY,
  12 * 60 * 60 * 1000 // 12 hours cache duration (updated from 1 hour)
);

export const UserService = {
  async getUsers(): Promise<User[]> {
    return await usersCache.getData();
  },

  async refreshUsers(): Promise<User[]> {
    return await usersCache.forceRefresh();
  },

  async getUserById(id: number): Promise<User> {
    const response = await axiosInstance.get<APIResponse<User>>(`${API_BASE_URL}/${id}`);
    const user = response.data.data;
    storeUserData(user);
    return user;
  },

  async getUserByUsername(username: string): Promise<User | null> {
    try {
      const response = await axiosInstance.get<APIResponse<User[]>>(`${API_BASE_URL}/username/${username}`);
      const users = response.data.data;
      if (!users || users.length === 0) {
        return null;
      }
      const user = users[0];
      storeUserData(user);
      return user;
    } catch (error) {
      console.error('Error in getUserByUsername:', error);
      return null;
    }
  },

  async getUserByOktaId(oktaId: string): Promise<User | null> {
    try {
      const response = await axiosInstance.get<APIResponse<User>>(`${API_BASE_URL}/okta/${oktaId}`);
      const user = response.data.data;
      if (!user) {
        return null;
      }
      storeUserData(user);
      return user;
    } catch (error) {
      console.error('Error in getUserByOktaId:', error);
      return null;
    }
  },

  async createUser(userData: UserCreate): Promise<User> {
    const response = await axiosInstance.post<APIResponse<User>>(API_BASE_URL, userData);
    const user = response.data.data;
    storeUserData(user);
    return user;
  },

  async updateUser(id: number, userData: UserUpdate): Promise<User> {
    const response = await axiosInstance.put<APIResponse<User>>(`${API_BASE_URL}/${id}`, userData);
    const user = response.data.data;
    storeUserData(user);
    return user;
  },

  async deleteUser(id: number): Promise<void> {
    await axiosInstance.delete(`${API_BASE_URL}/${id}`);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem("userId");
  },

  getUserId(): string | null {
    const storedId = localStorage.getItem("userId");
    if (storedId) {
      return storedId;
    }
    return null;
  },

  getUserData(): StoredUserData | null {
    const encryptedData = localStorage.getItem(STORAGE_KEY);
    if (!encryptedData) {
      return null;
    }
    
    const userData = decryptData(encryptedData);
    if (!userData) {
      return null;
    }

    // Check if data is stale (12 hours)
    if (Date.now() - userData.timestamp > 12 * 60 * 60 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return userData;
  },

  shouldRefreshUserData(): boolean {
    const userData = this.getUserData();
    if (!userData) return true;
    
    // Check if the data is older than 12 hours
    const twelveHoursAgo = Date.now() - (12 * 60 * 60 * 1000);
    return userData.timestamp < twelveHoursAgo;
  }
};
